import { useTranslation } from "react-i18next";
import { RadioGroupButtons, RadioGroupButtonItem, cn } from "@tellescope/ui";
import { formatShopPrice } from "../formatShopPrice";
import type { ShopPriceVariant } from "../types";

interface BillingToggleProps {
  variants: ShopPriceVariant[];
  value: string;
  onChange: (stripePriceId: string) => void;
  /** Compact variant for inline use inside a cart row. */
  compact?: boolean;
  className?: string;
  "aria-label": string;
}

/**
 * Toggling this control changes which Stripe price variant a cart item points to (each variant
 * resolves to its own Tellescope Product record — see cartStore's setCartItemVariant) rather than
 * setting a frequency field, since prepare_stripe_checkout resolves prices per-Product.
 */
export const BillingToggle = ({
  variants,
  value,
  onChange,
  compact = false,
  className,
  "aria-label": ariaLabel,
}: BillingToggleProps) => {
  const { t } = useTranslation("shop");
  return (
    <RadioGroupButtons
      value={value}
      onValueChange={onChange}
      aria-label={ariaLabel}
      className={cn(
        "grid gap-1 p-1 rounded-2xl bg-muted border border-border",
        variants.length === 1
          ? "grid-cols-1"
          : variants.length === 2
          ? "grid-cols-2"
          : variants.length === 3
          ? "grid-cols-3"
          : "grid-cols-4",
        className
      )}
    >
      {variants.map((variant) => {
        const selected = value === variant.stripePriceId;
        return (
          <RadioGroupButtonItem
            key={variant.stripePriceId}
            value={variant.stripePriceId}
            data-test={`billing-${variant.stripePriceId}`}
            className={cn(
              "flex flex-col items-start text-left h-auto rounded-xl transition-all",
              compact ? "px-3 py-1.5" : "px-4 py-2.5",
              selected ? "bg-card shadow-sm border border-border" : "border border-transparent hover:bg-card/50"
            )}
          >
            <span
              className={cn(
                "font-semibold",
                compact ? "text-xs" : "text-sm",
                selected ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {formatShopPrice(t, variant)}
            </span>
          </RadioGroupButtonItem>
        );
      })}
    </RadioGroupButtons>
  );
};
